import type { ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import * as Icons from 'lucide-react';
import type { MerchantNavSection } from './nav';
import { MerchantLogo } from './MerchantLogo';

interface MerchantSidebarProps {
  badge: string; // 'ONBOARDING' | 'COMÉRCIO'
  nav: readonly MerchantNavSection[];
  user: { name: string; role: string };
  onLogout: () => void;
}

function Icon({ name }: { name: string }): JSX.Element {
  const C = (Icons as unknown as Record<string, ComponentType<{ size?: number }>>)[name] ?? Icons.Circle;
  return <C size={18} />;
}

export function MerchantSidebar({ badge, nav, user, onLogout }: MerchantSidebarProps): JSX.Element {
  return (
    <aside className="m-inverse flex w-64 shrink-0 flex-col p-4" aria-label="Barra lateral">
      <div className="flex items-center gap-2 px-2 py-3">
        <MerchantLogo className="h-7 w-auto" />
        <span className="rounded-[var(--m-r-pill)] bg-[var(--m-brand)] px-2 py-0.5 text-[10px] font-bold tracking-[0.06em] text-white">{badge}</span>
      </div>
      <nav className="mt-2 flex-1 space-y-4" aria-label="Navegação">
        {nav.map((section, i) => (
          <div key={section.title ?? i}>
            {section.title && <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--m-fg-subtle)]">{section.title}</p>}
            <ul className="space-y-1">
              {section.items.map((item) => (
                <li key={item.label}>
                  {item.wip || !item.to ? (
                    <span className="flex cursor-not-allowed items-center gap-3 rounded-[var(--m-r-md)] px-3 py-2 text-sm text-[var(--m-fg-subtle)] opacity-60" title="Em breve">
                      <Icon name={item.icon} /> {item.label}
                    </span>
                  ) : (
                    <NavLink to={item.to} end={item.to === '/'} className={({ isActive }) => `flex items-center gap-3 rounded-[var(--m-r-md)] px-3 py-2 text-sm ${isActive ? 'bg-[var(--m-brand)] text-white' : 'text-[var(--m-fg-on-inverse)] hover:bg-white/10'}`}>
                      <Icon name={item.icon} />
                      <span className="flex-1">{item.label}</span>
                      {item.badge != null && item.badge > 0 && (
                        <span className="rounded-[var(--m-r-pill)] bg-white/15 px-1.5 py-0.5 text-[11px] font-bold leading-none">{item.badge}</span>
                      )}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3">
        <div>
          <p className="text-sm font-medium">{user.name}</p>
          <p className="text-xs text-[var(--m-fg-subtle)]">{user.role}</p>
        </div>
        <button onClick={onLogout} aria-label="Sair" className="rounded-[var(--m-r-md)] p-2 hover:bg-white/10"><Icons.LogOut size={18} /></button>
      </div>
    </aside>
  );
}
